import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Edit, Bot,  Building2, X, MapPin, CreditCard, Save, Loader2, AlertCircle, Upload, Phone, Mail, Globe, Tag, ChevronDown, CheckSquare, Briefcase, Plus, Edit3, User, LogIn, ExternalLink, FileText, Search, Users, LayoutTemplate, Hash } from 'lucide-react';
import { Client, AccessProfile, UserRole, User as AppUser, AutomationTemplate } from '../types';
import { generateIndustryName } from '../services/geminiService';
import {
    supabase_update_client,
    supabase_insert_client,
    supabase_get_next_client_bullhorn_id,
    extractErrorMessage,
    supabase_upload_image,
    supabase,
    supabase_get_client_shortlist_bundle,
    supabase_get_templates,
    supabase_update_application_status,
    supabase_log_interaction,
} from '../services/supabaseService';
import { formatClientNameWithFourDigit, formatClientFourDigitId } from '../utils/clientDisplay';
import { filterClientShortlistBundle, filterClientVacanciesList } from '../utils/clientShortlistSearch';
import { sendUserInvite } from '../services/notificationService';
import AiRichTextEditor from './AiRichTextEditor';
import { CandidatePipelineStrip, CandidateShortlistPhasesPanel, ClientVacancyListTable } from './talentPool/CandidateShortlistJourney';
import CandidateMoveModal from './CandidateMoveModal';
import { DEFAULT_KANBAN_STAGES } from '../constants';
import { getCandidateEmail } from '../utils/candidateContact';
import { triggerAutomation } from '../services/automationService';
import {
    pipelineStepToShortlistPhase,
    type PipelineStepId,
    type ShortlistPhaseId,
} from '../utils/candidateJourneyBuckets';

interface ClientDetailModalProps {
    client: Client | null;
    accessProfiles: AccessProfile[];
    currentUserRole: UserRole | null;
    currentUserClientId: string | null;
    onClose: () => void;
    onSaveSuccess: (newClient?: any) => void;
    initialTab?: 'general' | 'address' | 'invoice' | 'vacancies' | 'shortlist' | 'users' | 'content';
    onNavigateVacancy?: (vacancyId: string) => void;
    /** Opent tab Relaties (CRM) met uitgebreid bedrijfsprofiel. */
    onNavigateToCrm?: (clientId: string) => void;
    initialName?: string;
}

const ClientDetailModal: React.FC<ClientDetailModalProps> = ({
    client,
    accessProfiles,
    currentUserRole,
    currentUserClientId,
    onClose,
    onSaveSuccess,
    initialTab = 'general',
    initialName = '',
    onNavigateVacancy,
    onNavigateToCrm,
}) => {
    const isNew = !client;
    const [isEditing, setIsEditing] = useState(isNew);
    const [activeTab, setActiveTab] = useState<'general' | 'address' | 'invoice' | 'vacancies' | 'shortlist' | 'users' | 'content'>(initialTab);
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isVisible, setIsVisible] = useState(false);
    
    // AI Logo states
    const [showLogoSearch, setShowLogoSearch] = useState(false);
    const [isSearchingLogo, setIsSearchingLogo] = useState(false);
    const [logoOptions, setLogoOptions] = useState<any[]>([]);
    const [logoSearchError, setLogoSearchError] = useState('');
    
    const handleSearchLogo = async () => {
        if (!formData.name) return;
        setIsSearchingLogo(true);
        setLogoSearchError('');
        try {
            const { data: authData } = await supabase.auth.getUser();
            const { data: profile } = await supabase.from('profiles').select('client_id').eq('id', authData.user?.id).single();
            const clientId = profile?.client_id || '';
            const domain = formData.website ? formData.website.replace(/^https?:\/\//, '').replace(/\/.*$/, '') : '';
            const url = `${(import.meta as any).env.VITE_SERVER_URL || ''}/api/logo-search?q=${encodeURIComponent(formData.name)}&clientId=${clientId}&website=${encodeURIComponent(domain)}`;
            const res = await fetch(url);
            if (!res.ok) throw new Error(`API error`);
            const data = await res.json();
            setLogoOptions(data.images || []);
            if (!data.images || data.images.length === 0) setLogoSearchError('Geen logos gevonden.');
        } catch (e) {
            setLogoSearchError('Kan geen logos ophalen.');
        } finally {
            setIsSearchingLogo(false);
        }
    };
    
    const handleSelectAILogo = (url: string) => {
        setFormData({ ...formData, logo_url: url });
        setLogoPreview(url);
        setShowLogoSearch(false);
    };

    useEffect(() => {
        if (showLogoSearch && logoOptions.length === 0 && !isSearchingLogo && !logoSearchError) {
            handleSearchLogo();
        }
    }, [showLogoSearch]);

    useEffect(() => {
        setIsVisible(true);
    }, []);

    const handleClose = () => {
        setIsVisible(false);
        setTimeout(onClose, 300);
    };

    const [logoFile, setLogoFile] = useState<File | null>(null);
    const [logoPreview, setLogoPreview] = useState<string | null>(null);

    const [formData, setFormData] = useState<Partial<Client>>({
        name: initialName || '', industry: '', website: '', phone: '', email: '', contact_person: '',
        client_type: 'Direct', address_street: '', address_city: '', address_zip: '',
        invoice_kvk: '', invoice_vat: '', invoice_email: '', logo_url: '', default_access_profile_id: '',
        organizationDescription: '', vacancyStructure: ['Intro', 'Functieomschrijving', 'Werkzaamheden', 'Functie-eisen', 'Meer over {{bedrijfsnaam}}', 'Overige informatie']
    });

    const [inviteUserEmail, setInviteUserEmail] = useState('');
    const [inviteUserName, setInviteUserName] = useState('');

    const [clientVacancies, setClientVacancies] = useState<any[]>([]);
    const [isLoadingVacancies, setIsLoadingVacancies] = useState(!!client?.id);
    const [clientUsers, setClientUsers] = useState<any[]>([]);
    const [availableCompanies, setAvailableCompanies] = useState<any[]>([]);

    const [clientApplications, setClientApplications] = useState<any[]>([]);
    const [clientInterviews, setClientInterviews] = useState<any[]>([]);
    const [clientPlacements, setClientPlacements] = useState<any[]>([]);
    const [clientStageHistory, setClientStageHistory] = useState<any[]>([]);
    const [shortlistScope, setShortlistScope] = useState<'current' | 'all'>('all');
    const [shortlistFocusPhase, setShortlistFocusPhase] = useState<ShortlistPhaseId | null>(null);
    const [shortlistFocusKey, setShortlistFocusKey] = useState(0);
    const [isLoadingShortlist, setIsLoadingShortlist] = useState(!!client?.id);

    const [showMoveModal, setShowMoveModal] = useState(false);
    const [moveDetails, setMoveDetails] = useState<any>(null);
    const [moveTemplates, setMoveTemplates] = useState<AutomationTemplate[]>([]);
    const [moveUser, setMoveUser] = useState<AppUser | null>(null);

    useEffect(() => {
        if (!isVisible || client) return;
        let cancelled = false;
        supabase_get_next_client_bullhorn_id()
            .then((nextId) => {
                if (!cancelled) {
                    setFormData((prev) => ({
                        ...prev,
                        bullhorn_id: prev.bullhorn_id || nextId,
                    }));
                }
            })
            .catch((e) => console.warn('[ClientDetailModal] next bullhorn_id', e));
        return () => {
            cancelled = true;
        };
    }, [isVisible, client]);

    useEffect(() => {
        const fetchCompanies = async () => {
            try {
                const { data } = await supabase.from('clients').select('id, name, logo_url, invoice_kvk, data, bullhorn_id').order('name');
                if (data) setAvailableCompanies(data);
            } catch(e) {}
        };
        if (isVisible) {
           fetchCompanies();
        }
    }, [isVisible]);
    const [vacancySearch, setVacancySearch] = useState('');
    const [shortlistSearch, setShortlistSearch] = useState('');

    const [isSearchingKvk, setIsSearchingKvk] = useState(false);
    const [isGeneratingIndustry, setIsGeneratingIndustry] = useState(false);
    const [kvkResults, setKvkResults] = useState<any[]>([]);
    const [kvkSearchedEmpty, setKvkSearchedEmpty] = useState<string | null>(null);

    const handleSearchKvk = async () => {
        if (!formData.name) return;
        setIsSearchingKvk(true);
        setKvkSearchedEmpty(null);
        try {
            const res = await fetch(`${(import.meta as any).env.VITE_SERVER_URL || ''}/api/kvk/search?query=${encodeURIComponent(formData.name)}`);
            const text = await res.text();
            try {
                const data = JSON.parse(text);
                if (res.status === 500 || data.error) {
                    setKvkResults([]);
                    const errStr = data.details ? String(data.details) : '';
                    if (errStr.includes("401") || errStr.includes("Unauthorized")) {
                        setKvkSearchedEmpty("Geen KVK rechten.");
                    } else {
                        setKvkSearchedEmpty(`KVK Fout: ${data.error || 'Server error'}`);
                    }
                } else if (data.resultaten && data.resultaten.length > 0) {
                    setKvkResults(data.resultaten);
                } else {
                    setKvkResults([]);
                    setKvkSearchedEmpty("Geen KVK resultaten gevonden.");
                }
            } catch(e) {
                setKvkSearchedEmpty("API configuratie fout.");
                setKvkResults([]);
            }
        } catch (e) {
            setKvkSearchedEmpty("Netwerkfout KVK API.");
            setKvkResults([]);
        } finally {
            setIsSearchingKvk(false);
        }
    };

    const handleSelectKvkResult = async (result: any) => {
    const prev = formData;
    setKvkResults([]);
    setKvkSearchedEmpty(null);
    let newWebsite = prev.website;
    let newPhone = prev.phone;
    let newEmail = prev.email;
    let newIndustry = prev.industry;
    let newZip = prev.address_zip;
    let newCity = prev.address_city;
    let newStreet = prev.address_street;
    
    if (result.kvkNummer) {
        try {
            const res = await fetch(`${(import.meta as any).env.VITE_SERVER_URL || ''}/api/kvk/profile?kvkNumber=${result.kvkNummer}`);
            if (res.ok) {
                const profileStr = await res.text();
                try {
                    const profileData = JSON.parse(profileStr);
                    if (profileData && !profileData.error) {
                        const sbiArr = profileData.sbiActiviteiten || [];
                        const initialIndustry = newIndustry;
                          if (sbiArr.length > 0 && !newIndustry) newIndustry = sbiArr[0].sbiOmschrijving;
                          
                          if (newIndustry && newIndustry !== initialIndustry) {
                              setIsGeneratingIndustry(true);
                              generateIndustryName(result.naam || result.handelsnaam, newIndustry).then(aiIndustry => {
                                  if (aiIndustry && aiIndustry !== newIndustry) {
                                      setFormData((pf: any) => ({...pf, industry: aiIndustry}));
                                  }
                              }).catch(e => console.error(e)).finally(() => setIsGeneratingIndustry(false));
                          }
                        const websites = profileData._embedded?.hoofdvestiging?.websites || [];
                        if (websites.length > 0 && !newWebsite) newWebsite = websites[0];
                        const phones = profileData._embedded?.hoofdvestiging?.telefoonnummers || [];
                        if (phones.length > 0 && !newPhone) newPhone = phones[0];
                        const emails = profileData._embedded?.hoofdvestiging?.emailadressen || [];
                        if (emails.length > 0 && !newEmail) newEmail = emails[0];
                        
                        const bezoeker = profileData._embedded?.hoofdvestiging?.adressen?.find((a:any) => a.type === 'bezoekadres' || a.type === 'correspondentieadres');
                        if (bezoeker) {
                            if (bezoeker.postcode && (!newZip || newZip === prev.address_zip)) newZip = bezoeker.postcode;
                            if (bezoeker.plaats && (!newCity || newCity === prev.address_city)) newCity = bezoeker.plaats;
                            if (bezoeker.straatnaam) {
                                newStreet = `${bezoeker.straatnaam} ${bezoeker.huisnummer||''}${bezoeker.huisnummerToevoeging||''}`.trim();
                            }
                        }
                    }
                } catch(e) {}
            }
        } catch(e) {}
    }
    
    setFormData({
        ...prev,
        name: result.handelsnaam || result.naam || prev.name,
        industry: newIndustry || prev.industry,
        website: newWebsite,
        phone: newPhone,
        email: newEmail,
        address_street: newStreet || (result.adres?.binnenlandsAdres?.straatnaam ? `${result.adres.binnenlandsAdres.straatnaam} ${result.adres.binnenlandsAdres.huisnummer||''}${result.adres.binnenlandsAdres.huisnummerToevoeging||''}`.trim() : (result.straatnaam ? `${result.straatnaam} ${result.huisnummer||''}`.trim() : prev.address_street)),
        address_zip: newZip || result.adres?.binnenlandsAdres?.postcode || result.postcode || prev.address_zip,
        address_city: newCity || result.adres?.binnenlandsAdres?.plaats || result.plaats || prev.address_city,
        invoice_kvk: result.kvkNummer || prev.invoice_kvk
    });
};


    const companyFourDigit = useMemo(() => formatClientFourDigitId(formData), [formData]);
    const shortlistCount = useMemo(() => clientApplications.length, [clientApplications]);

    const resolveVacancyClient = useCallback(
        (clientId: string | null | undefined, embed: any) => {
            const row = Array.isArray(embed) ? embed[0] : embed;
            if (row?.name || row?.logo_url) return row;
            const id = String(clientId || row?.id || '').trim();
            if (!id) return null;
            const c = availableCompanies.find((x) => x.id === id);
            if (c) return { name: c.name, logo_url: c.logo_url };
            if (id === client?.id) return { name: formData.name, logo_url: formData.logo_url };
            return null;
        },
        [availableCompanies, client?.id, formData.name, formData.logo_url]
    );

    const filteredClientVacancies = useMemo(
        () => filterClientVacanciesList(clientVacancies, vacancySearch, resolveVacancyClient),
        [clientVacancies, vacancySearch, resolveVacancyClient]
    );

    const filteredShortlistBundle = useMemo(
        () =>
            filterClientShortlistBundle(
                shortlistSearch,
                clientApplications,
                clientInterviews,
                clientPlacements,
                clientStageHistory
            ),
        [shortlistSearch, clientApplications, clientInterviews, clientPlacements, clientStageHistory]
    );

    const loadClientVacancies = useCallback(async (clientId: string) => {
        setIsLoadingVacancies(true);
        try {
            const { data, error } = await supabase
                .from('vacancies')
                .select(
                    `id, title, status, vacancy_number, created_at, data, opdrachtgever_id, eindklant_id, client_id,
                    opdrachtgever:clients!vacancies_opdrachtgever_id_fkey(id, name, logo_url),
                    eindklant:clients!vacancies_eindklant_id_fkey(id, name, logo_url)`
                )
                .or(`client_id.eq.${clientId},opdrachtgever_id.eq.${clientId},eindklant_id.eq.${clientId}`)
                .order('created_at', { ascending: false });
            if (error) throw error;
            setClientVacancies(
                (data || []).map((v: any) => ({
                    ...v,
                    location: v.location || v.data?.location || '—',
                }))
            );
        } catch (e) {
            console.error(e);
            setClientVacancies([]);
        } finally {
            setIsLoadingVacancies(false);
        }
    }, []);

    const loadClientShortlistBundle = useCallback(async (clientId: string) => {
        setIsLoadingShortlist(true);
        try {
            const bundle = await supabase_get_client_shortlist_bundle(clientId);
            setClientApplications(bundle.applications);
            setClientInterviews(bundle.interviews);
            setClientPlacements(bundle.placements);
            setClientStageHistory(bundle.stageHistory);
        } catch (e) {
            console.error(e);
        } finally {
            setIsLoadingShortlist(false);
        }
    }, []);

    const openVacancy = useCallback(
        (vacancyId: string) => {
            if (!vacancyId) return;
            if (onNavigateVacancy) onNavigateVacancy(vacancyId);
            else window.location.href = `?vacancyId=${vacancyId}`;
        },
        [onNavigateVacancy]
    );

    const openCrmCompanyDetails = useCallback(() => {
        const id = client?.id;
        if (!id) return;
        handleClose();
        window.setTimeout(() => {
            if (onNavigateToCrm) onNavigateToCrm(id);
            else window.dispatchEvent(new CustomEvent('open-client', { detail: id }));
        }, 320);
    }, [client?.id, onNavigateToCrm]);

    const openShortlistMoveForApplication = useCallback(
        (app: any) => {
            const vac = app.vacancies;
            const cand = app.candidates;
            setMoveDetails({
                applicationId: app.id,
                candidateId: app.candidate_id,
                candidateRealId: app.candidate_id,
                candidateName: cand?.name || 'Kandidaat',
                candidateEmail: getCandidateEmail(cand),
                fromStage: app.status || 'Shortlist',
                toStage: app.status || 'Shortlist',
                vacancyId: app.vacancy_id,
                vacancyTitle: vac?.title || '',
                clientName: formData.name || '',
            });
            setShowMoveModal(true);
        },
        [formData.name]
    );

    const handleConfirmShortlistMove = async (
        channel: 'email' | 'whatsapp' | 'log',
        _templateId: string,
        _subject: string,
        messageContent: string,
        internalNoteContent: string,
        targetStage?: string,
        _asDraft?: boolean,
        rejectionReason?: string
    ) => {
        if (!moveDetails || !client?.id) return;
        try {
            const finalTargetStage = targetStage || moveDetails.toStage;
            const targetStageObj =
                DEFAULT_KANBAN_STAGES.find((s) => s.name === finalTargetStage || s.id === finalTargetStage) || {
                    id: finalTargetStage,
                    name: finalTargetStage,
                };
            await supabase_update_application_status(
                moveDetails.candidateId || moveDetails.applicationId,
                targetStageObj.id
            );
            await triggerAutomation('stage_changed', {
                vacancyId: moveDetails.vacancyId,
                candidateId: moveDetails.candidateRealId,
                stageId: targetStageObj.id,
                stageName: targetStageObj.name,
                user_id: moveUser?.id,
            });
            const note = rejectionReason
                ? `${internalNoteContent || ''}\n\nAfwijzingsreden: ${rejectionReason}`.trim()
                : internalNoteContent;
            const actor = moveUser?.name || 'Recruiter';
            await supabase_log_interaction(
                moveDetails.candidateRealId,
                client.id,
                'system',
                `(Status Update) ${actor} heeft kandidaat verplaatst naar '${finalTargetStage}'.${rejectionReason ? ` Reden: ${rejectionReason}` : ''}`
            );
            if (note?.trim()) {
                await supabase_log_interaction(
                    moveDetails.candidateRealId,
                    client.id,
                    'note',
                    `[Verplaatst naar ${finalTargetStage}] ${note}`
                );
            }
            if (channel !== 'log' && messageContent) {
                await supabase_log_interaction(
                    moveDetails.candidateRealId,
                    client.id,
                    channel === 'whatsapp' ? 'whatsapp' : 'email',
                    messageContent
                );
            }
            setShowMoveModal(false);
            setMoveDetails(null);
            await loadClientShortlistBundle(client.id);
        } catch (e) {
            console.error(e);
            alert('Status kon niet worden bijgewerkt.');
        }
    };

    useEffect(() => {
        supabase_get_templates().then((t) => setMoveTemplates(t || []));
        supabase.auth.getUser().then(async ({ data }) => {
            const uid = data.user?.id;
            if (!uid) return;
            const { data: profile } = await supabase.from('profiles').select('id, name, email, client_id').eq('id', uid).single();
            if (profile) {
                setMoveUser({
                    id: profile.id,
                    name: profile.name || data.user?.email || 'Recruiter',
                    email: profile.email || data.user?.email || '',
                    role: UserRole.RECRUITER,
                    clientId: profile.client_id,
                } as AppUser);
            }
        });
    }, []);

    useEffect(() => {
        if (client) {
            setFormData({ ...client });
            if (client.logo_url) setLogoPreview(client.logo_url);

            if (client.id) {
                if (!client.industry && !client.address_city) {
                    supabase
                        .from('clients')
                        .select('*')
                        .eq('id', client.id)
                        .single()
                        .then(({ data }) => {
                            if (data) {
                                setFormData((prev) => ({ ...prev, ...data }));
                                if (data.logo_url) setLogoPreview(data.logo_url);
                            }
                        });
                }

                setIsLoadingVacancies(true);
                setIsLoadingShortlist(true);
                void loadClientVacancies(client.id);
                void loadClientShortlistBundle(client.id);
                supabase
                    .from('profiles')
                    .select('*')
                    .eq('client_id', client.id)
                    .then(({ data }) => {
                        if (data) setClientUsers(data);
                    });
            }
        }
    }, [client, loadClientVacancies, loadClientShortlistBundle]);

    useEffect(() => {
        if (activeTab === 'shortlist' && client?.id) void loadClientShortlistBundle(client.id);
    }, [activeTab, client?.id, loadClientShortlistBundle]);

    const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setLogoFile(file);
            setLogoPreview(URL.createObjectURL(file));
        }
    };

    const handleSubmit = async (e?: React.FormEvent) => {
        if (e) e.preventDefault();
        setIsProcessing(true);
        setError(null);

        try {
            let finalLogoUrl = formData.logo_url;
            if (logoFile) {
                finalLogoUrl = await supabase_upload_image(logoFile);
            }

            const payload = { ...formData, logo_url: finalLogoUrl };
            Object.keys(payload).forEach(key => (payload as any)[key] === undefined && delete (payload as any)[key]);

            if (client) {
                await supabase_update_client(client.id, payload);
                onSaveSuccess({ ...payload, id: client.id });
            } else {
                if (currentUserRole === UserRole.CLIENT && currentUserClientId) {
                    (payload as any).parent_client_id = currentUserClientId;
                }

                const newClient = await supabase_insert_client(payload);

                if (newClient && inviteUserEmail && inviteUserName) {
                    try {
                        const apiUrl = window.location.hostname === 'localhost' ? '/api/users' : '/api/users';
                        let initialVisibleTabs: string[] = [];
                        if (formData.default_access_profile_id) {
                            const selectedProfile = accessProfiles.find(p => p.id === formData.default_access_profile_id);
                            if (selectedProfile) {
                                initialVisibleTabs = selectedProfile.visibleTabs;
                            }
                        }

                        await fetch(apiUrl, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                                email: inviteUserEmail,
                                role: UserRole.CLIENT,
                                name: inviteUserName,
                                client_id: newClient.id,
                                visible_tabs: initialVisibleTabs
                            })
                        });

                        try {
                            await sendUserInvite({
                                to: inviteUserEmail,
                                name: inviteUserName,
                                role: UserRole.CLIENT
                            });
                        } catch (e) { console.warn("Email error", e); }
                    } catch (inviteError) {
                        console.error("Invite error:", inviteError);
                    }
                }
                onSaveSuccess(newClient);
                return;
            }
        } catch (err: any) {
            console.error("Save client error:", err);
            setError(extractErrorMessage(err));
        } finally {
            setIsProcessing(false);
        }
    };

    const NavButton = ({
        icon,
        label,
        active,
        onClick,
        color,
        loading,
    }: {
        icon: React.ReactNode;
        label: string;
        active: boolean;
        onClick: () => void;
        color?: string;
        loading?: boolean;
    }) => (
        <button
            onClick={onClick}
            disabled={loading}
            className={`flex items-center gap-2 px-1 py-4 text-xs font-black uppercase tracking-widest transition-all border-b-2 whitespace-nowrap ${active ? 'border-[#e51b23] text-[#e51b23]' : 'border-transparent text-slate-500 hover:text-slate-800'} ${loading ? 'opacity-80' : ''}`}
        >
            <div className={`flex items-center gap-2 ${color ? color : ''}`}>
                {loading ? <Loader2 size={14} className="animate-spin text-[#e51b23] shrink-0" /> : icon}
                {label}
            </div>
        </button>
    );

    const SectionHeader = ({ icon, title }: { icon: React.ReactNode, title: string }) => (
        <h3 className="text-sm font-black text-slate-900 uppercase tracking-widest mb-6 flex items-center gap-2">
            <span className="text-[#e51b23]">{icon}</span> {title}
        </h3>
    );

    return (
        <div className="fixed inset-0 z-[999] flex justify-end">
            <div className={`absolute inset-0 bg-slate-900/40 backdrop-blur-sm transition-opacity duration-300 ${isVisible ? 'opacity-100' : 'opacity-0'}`} onClick={handleClose} />
            
            <div className={`relative w-full sm:w-3/4 h-full bg-slate-50 shadow-2xl flex flex-col transform transition-transform duration-300 ease-in-out ${isVisible ? 'translate-x-0' : 'translate-x-full'}`}>

                {/* COMPACT HEADER */}
                <div className="px-8 py-6 bg-white z-10 shrink-0 flex items-center justify-between gap-4 border-b border-slate-200">
                    <div className="flex items-center gap-6 min-w-0">
                        <div className="w-16 h-16 rounded-xl bg-slate-50 flex items-center justify-center text-slate-400 shrink-0 border border-slate-200 p-1 shadow-sm overflow-hidden">
                            {logoPreview ? (
                                <img src={logoPreview} className="w-full h-full object-contain" />
                            ) : (
                                <Building2 size={32} className="text-slate-300" />
                            )}
                        </div>
                        <div className="min-w-0">
                            <h2 className="text-2xl font-black text-slate-900 tracking-tight truncate leading-tight flex items-center gap-2 flex-wrap">
                                {isNew ? 'Nieuwe Organisatie' : formData.name}
                                {!isNew && (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-mono font-black text-slate-500 bg-slate-100 border border-slate-200 rounded tracking-widest shrink-0">
                                        <Hash size={10} className="text-slate-400" />
                                        {companyFourDigit}
                                    </span>
                                )}
                            </h2>
                            <p className="text-sm text-slate-500 font-medium truncate flex items-center gap-2 mt-1">
                                {formData.client_type === 'Agency' ? 'Partner Bureau' : 'Directe Klant'}
                                {formData.address_city && <span className="hidden sm:inline opacity-50">• {formData.address_city}</span>}
                            </p>
                            {!isNew && client?.id && (
                                <button
                                    type="button"
                                    onClick={openCrmCompanyDetails}
                                    className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-widest text-[#e51b23] hover:text-red-700 hover:underline transition-colors"
                                >
                                    <ExternalLink size={12} />
                                    Uitgebreid profiel in Relaties
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="flex items-center gap-3 shrink-0">
                        {!isNew && client?.id && !isEditing && (
                            <button
                                type="button"
                                onClick={openCrmCompanyDetails}
                                className="hidden sm:flex items-center gap-2 px-4 py-2.5 rounded-lg font-black text-xs uppercase tracking-widest border border-slate-200 bg-white text-slate-700 hover:border-[#e51b23] hover:text-[#e51b23] hover:bg-red-50/50 transition-all"
                                title="Open uitgebreid bedrijfsprofiel in Relaties (CRM)"
                            >
                                <ExternalLink size={14} /> Relaties
                            </button>
                        )}
                        {isEditing ? (
                            <>
                                <button onClick={() => setIsEditing(false)} className="text-sm font-bold text-slate-500 hover:text-slate-700 px-3 transition-colors" title="Bewerken annuleren">
                                    Annuleren
                                </button>
                                <button
                                    onClick={handleSubmit}
                                    disabled={isProcessing}
                                    className="bg-[#e51b23] text-white px-5 py-2.5 rounded-lg font-black text-xs uppercase tracking-widest hover:bg-red-700 transition-all active:scale-95 flex items-center gap-2 disabled:opacity-50 shadow-sm"
                                >
                                    {isProcessing ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Opslaan
                                </button>
                            </>
                        ) : (
                            <button onClick={() => setIsEditing(true)} className="bg-[#f0f0f0] text-slate-900 px-5 py-2.5 rounded-lg font-black text-xs uppercase tracking-widest hover:bg-slate-200 transition-all active:scale-95 flex items-center gap-2">
                                <Edit size={16} /> Bewerk Details
                            </button>
                        )}
                        <div className="w-px h-6 bg-slate-200 mx-1"></div>
                        <button onClick={handleClose} className="p-2.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">
                            <X size={20} />
                        </button>
                    </div>
                </div>

                {!isNew && client?.id && (
                    <div className="shrink-0 border-b border-slate-100 bg-white">
                        <CandidatePipelineStrip
                            applications={clientApplications}
                            interviews={clientInterviews}
                            placements={clientPlacements}
                            stageHistory={clientStageHistory}
                            scope={shortlistScope}
                            loading={isLoadingShortlist}
                            onOpenShortlistTab={(step: PipelineStepId) => {
                                setActiveTab('shortlist');
                                setShortlistFocusPhase(pipelineStepToShortlistPhase(step));
                                setShortlistFocusKey((k) => k + 1);
                            }}
                        />
                    </div>
                )}

                {/* TABS SECTION */}
                <div className="px-8 bg-white border-b border-slate-200 shrink-0">
                    <div className="flex gap-8 overflow-x-auto custom-scrollbar">
                        <NavButton icon={<Building2 size={16} />} label="Bedrijfs Info" active={activeTab === 'general'} onClick={() => setActiveTab('general')} />
                        <NavButton
                            icon={<Briefcase size={16} />}
                            label={
                                isLoadingVacancies
                                    ? 'Vacatures'
                                    : `Vacatures (${clientVacancies.length})`
                            }
                            loading={isLoadingVacancies}
                            active={activeTab === 'vacancies'}
                            onClick={() => setActiveTab('vacancies')}
                        />
                        {!isNew && (
                            <NavButton
                                icon={<LayoutTemplate size={16} />}
                                label={isLoadingShortlist ? 'Shortlist' : `Shortlist (${shortlistCount})`}
                                loading={isLoadingShortlist}
                                active={activeTab === 'shortlist'}
                                onClick={() => setActiveTab('shortlist')}
                            />
                        )}
                        <NavButton icon={<Users size={16} />} label="Contactpersonen" active={activeTab === 'users'} onClick={() => setActiveTab('users')} />
                        <NavButton icon={<CreditCard size={16} />} label="Financieel" active={activeTab === 'invoice'} onClick={() => setActiveTab('invoice')} />
                        <NavButton icon={<FileText size={16} />} label="Content" active={activeTab === 'content'} onClick={() => setActiveTab('content')} />
                    </div>
                </div>

                {/* Main Content Layout */}
                <div className="flex-1 overflow-y-auto custom-scrollbar p-6 bg-slate-50/50 relative min-h-0">
                    <div className="w-full h-full">
                        {error && (
                            <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-xl flex items-start gap-3 text-red-700 text-xs font-bold animate-slideDown">
                                <AlertCircle size={18} className="shrink-0" />
                                <span>{error}</span>
                            </div>
                        )}

                        {activeTab === 'general' && (
                            <div className="bg-white rounded-2xl p-8 border border-slate-200 shadow-sm w-full space-y-8 animate-fadeIn">
                                <SectionHeader icon={<Building2 size={16} />} title="Organisatie Details" />

                                <div className="flex items-start gap-8">
                                    <div className="shrink-0">
                                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">Logo</label>
                                        <div className="w-32 h-32 border-2 border-dashed border-slate-200 rounded-2xl flex flex-col items-center justify-center bg-slate-50 hover:bg-white hover:border-[#e51b23] transition-all cursor-pointer relative overflow-hidden group">
                                            {logoPreview ? (
                                                <img src={logoPreview} className="w-full h-full object-contain p-4" />
                                            ) : (
                                                <div className="text-center p-4">
                                                    <Upload size={24} className="mx-auto text-slate-300 mb-2 group-hover:text-[#e51b23] transition-colors" />
                                                    <span className="text-[10px] text-slate-400 font-bold uppercase">Upload</span>
                                                </div>
                                            )}
                                            <input type="file" accept="image/*" onChange={handleLogoUpload} className="absolute inset-0 opacity-0 cursor-pointer" />
                                        </div>
                                        {isEditing && (
                                            <button 
                                                type="button" 
                                                disabled={!formData.name}
                                                onClick={() => setShowLogoSearch(true)}
                                                className="mt-3 flex items-center justify-center gap-1.5 w-full py-2 bg-[#f0f0f0] hover:bg-slate-200 text-slate-700 hover:text-slate-900 rounded-lg text-[9px] font-black uppercase tracking-widest transition-colors"
                                            >
                                                <Search size={12} /> AI Zoek Logo
                                            </button>
                                        )}
                                    </div>

                                    <div className="flex-1 space-y-6">
                                        
                                        <div>
<label className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center justify-between mb-1.5">
    <span>Bedrijfsnaam *</span>
    {isEditing && <button onClick={handleSearchKvk} disabled={isSearchingKvk || !formData.name} className="flex items-center gap-2 px-3 py-1 bg-orange-50 border border-orange-100 hover:bg-orange-100 rounded-lg text-orange-600 hover:text-orange-800 disabled:opacity-50 transition-colors shadow-sm">
        {isSearchingKvk ? <Loader2 size={12} className="animate-spin" /> : <Search size={12} />} 
        <img src="https://xquintpfpzdsxupnhypj.supabase.co/storage/v1/object/public/public/logos/KVK_logo_RGB30%20(1).png" alt="KVK" className="h-3.5 object-contain opacity-90" />
    </button>}
</label>
<div className="relative">
    {isEditing ? <input type="text" required value={formData.name || ''} onChange={e => setFormData({ ...formData, name: e.target.value })} className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 outline-none focus:border-[#e51b23] focus:ring-1 focus:ring-[#e51b23] transition-all" /> : <p className="text-sm font-bold text-slate-900 py-1.5 break-words">{formData.name || '-'}</p>}
    {(kvkResults.length > 0 || kvkSearchedEmpty) && (
        <div className="absolute z-10 w-full mt-2 bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden max-h-64 flex flex-col">
            <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 text-[10px] font-black uppercase tracking-widest text-slate-500 flex justify-between items-center shrink-0">
                KVK Resultaten
                <button onClick={() => { setKvkResults([]); setKvkSearchedEmpty(null); }} className="hover:text-slate-700 transition-colors"><X size={12}/></button>
            </div>
            <div className="overflow-y-auto custom-scrollbar flex-1">
                {kvkResults.length > 0 ? kvkResults.map((res: any, i: number) => (
                    <div key={i} onClick={() => handleSelectKvkResult(res)} className="p-3 hover:bg-slate-50 cursor-pointer border-b border-slate-50 last:border-0 transition-colors">
                        <div className="text-sm font-bold text-slate-900">{res.naam || res.handelsnaam}</div>
                        <div className="text-xs text-slate-500">{res.adres?.binnenlandsAdres?.straatnaam || res.straatnaam} {res.adres?.binnenlandsAdres?.huisnummer || res.huisnummer}{res.adres?.binnenlandsAdres?.huisnummerToevoeging || res.huisnummerToevoeging || ''}, {res.adres?.binnenlandsAdres?.plaats || res.plaats}</div>
                        <div className="text-[10px] font-black text-slate-400 uppercase tracking-wider mt-1">KVK: {res.kvkNummer}</div>
                    </div>
                )) : (
                    <div className="p-6 flex flex-col items-center justify-center text-center gap-2">
                        <AlertCircle size={24} className="text-orange-400" />
                        <p className="text-sm font-bold text-slate-700">{kvkSearchedEmpty}</p>
                        <p className="text-xs text-slate-500">Controleer de spelling of pas de bedrijfsnaam aan.</p>
                    </div>
                )}
            </div>
        </div>
    )}
</div>
</div>

                                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                                            <div>
                                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">Bedrijfsnummer</label>
                                                <p className="text-sm font-mono font-black text-slate-700 py-1.5 tabular-nums">{companyFourDigit}</p>
                                            </div>
                                            <div>
                                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2 mb-1.5"><span>Industrie</span> {isGeneratingIndustry && <Loader2 size={12} className="animate-spin text-indigo-500" />}</label>
                                                {isEditing ? <input type="text" value={formData.industry || ''} onChange={e => setFormData({ ...formData, industry: e.target.value })} className="w-full p-2.5 shrink-0 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 outline-none focus:border-[#e51b23] focus:ring-1 focus:ring-[#e51b23] transition-all" /> : <p className="text-sm font-bold text-slate-900 py-1.5 break-words">{formData.industry || '-'}</p>}
                                            </div>
                                            <div>
                                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">Opdrachtgever / Ouderbedrijf</label>
                                                <div className="relative">
                                                    {isEditing ? <select value={formData.parent_client_id || ''} onChange={e => setFormData({ ...formData, parent_client_id: e.target.value || null })} className="w-full p-2.5 shrink-0 pr-10 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 outline-none cursor-pointer appearance-none focus:border-[#e51b23] focus:ring-1 focus:ring-[#e51b23] transition-all">
                                                        <option value="">Geen (Hoofdbedrijf)</option>
                                                        {availableCompanies.filter(c => c.id !== client?.id).map(c => (
                                                            <option key={c.id} value={c.id}>{formatClientNameWithFourDigit(c)}</option>
                                                        ))}
                                                    </select> : <p className="text-sm font-bold text-slate-900 py-1.5 break-words">{formData.parent_client_id ? formatClientNameWithFourDigit(availableCompanies.find(c => c.id === formData.parent_client_id) || { name: 'Geladen...' }) : '-'}</p>}
                                                    <ChevronDown size={14} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                                                </div>
                                            </div>
                                            <div>
                                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">Type</label>
                                                <div className="relative">
                                                    {isEditing ? <select value={formData.client_type} onChange={e => setFormData({ ...formData, client_type: e.target.value as any })} className="w-full p-2.5 shrink-0 pr-10 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 outline-none cursor-pointer appearance-none focus:border-[#e51b23] focus:ring-1 focus:ring-[#e51b23] transition-all">
                                                        <option value="Direct">Klant / Werkplek</option>
                                                        <option value="Subcontractor">Leverancier</option>
                                                    </select> : <p className="text-sm font-bold text-slate-900 py-1.5 break-words">{formData.client_type === 'Direct' ? 'Klant / Werkplek' : (formData.client_type === 'Subcontractor' ? 'Leverancier' : (formData.client_type || '-'))}</p>}
                                                    <ChevronDown size={14} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                                                </div>
                                            </div>
                                        </div>
                                        <div>
                                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">Website</label>
                                            {isEditing ? <input type="url" value={formData.website} onChange={e => setFormData({ ...formData, website: e.target.value })} className="w-full p-2.5 shrink-0 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 outline-none focus:border-[#e51b23] focus:ring-1 focus:ring-[#e51b23] transition-all" placeholder="https://" /> : (formData.website ? <a href={formData.website.startsWith('http') ? formData.website : `https://${formData.website}`} target="_blank" rel="noreferrer" className="text-sm font-bold text-indigo-600 hover:underline py-1.5 block break-all">{formData.website}</a> : <p className="text-sm font-bold text-slate-900 py-1.5">-</p>)}
                                        </div>



                                    <div className="pt-6 border-t border-slate-200 mt-6 pt-8">
                                        <SectionHeader icon={<MapPin size={16} />} title="Locatie & Contact" />
                                        <div className="space-y-6">
                                            <div>
                                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">Straat & Huisnummer</label>
                                                {isEditing ? <input type="text" value={formData.address_street || ''} onChange={e => setFormData({ ...formData, address_street: e.target.value })} className="w-full p-2.5 shrink-0 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 outline-none focus:border-[#e51b23] focus:ring-1 focus:ring-[#e51b23] transition-all" /> : <p className="text-sm font-bold text-slate-900 py-1.5 break-words">{formData.address_street || '-'}</p>}
                                            </div>
                                            <div className="grid grid-cols-2 gap-4">
                                                <div>
                                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">Postcode</label>
                                                    {isEditing ? <input type="text" value={formData.address_zip || ''} onChange={e => setFormData({ ...formData, address_zip: e.target.value })} className="w-full p-2.5 shrink-0 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 outline-none focus:border-[#e51b23] focus:ring-1 focus:ring-[#e51b23] transition-all" /> : <p className="text-sm font-bold text-slate-900 py-1.5 break-words">{formData.address_zip || '-'}</p>}
                                                </div>
                                                <div>
                                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">Stad</label>
                                                    {isEditing ? <input type="text" value={formData.address_city || ''} onChange={e => setFormData({ ...formData, address_city: e.target.value })} className="w-full p-2.5 shrink-0 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 outline-none focus:border-[#e51b23] focus:ring-1 focus:ring-[#e51b23] transition-all" /> : <p className="text-sm font-bold text-slate-900 py-1.5 break-words">{formData.address_city || '-'}</p>}
                                                </div>
                                            </div>

                                            <div className="pt-6 border-t border-slate-200"></div>

                                            <div className="grid grid-cols-2 gap-4">
                                                <div>
                                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">Primair Contactpersoon</label>
                                                    {isEditing ? (
    <select value={formData.contact_person || ''} onChange={e => setFormData({ ...formData, contact_person: e.target.value })} className="w-full p-2.5 shrink-0 pr-10 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 outline-none cursor-pointer appearance-none focus:border-[#e51b23] focus:ring-1 focus:ring-[#e51b23] transition-all">
        <option value="">(Niemand geregistreerd)</option>
        {clientUsers.map(u => {
            const fullName = `${u.first_name || ''} ${u.last_name || ''}`.trim();
            return <option key={u.id} value={fullName}>{fullName}</option>;
        })}
    </select>
) : (
    <p className="text-sm font-bold text-slate-900 py-1.5 break-words">{formData.contact_person || '-'}</p>
)}
                                                </div>
                                                <div>
                                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">Email</label>
                                                    {isEditing ? <input type="email" value={formData.email || ''} onChange={e => setFormData({ ...formData, email: e.target.value })} className="w-full p-2.5 shrink-0 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 outline-none focus:border-[#e51b23] focus:ring-1 focus:ring-[#e51b23] transition-all" /> : <p className="text-sm font-bold text-slate-900 py-1.5 break-words">{formData.email || '-'}</p>}
                                                </div>
                                            </div>
                                            <div>
                                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">Telefoonnummer</label>
                                                {isEditing ? <input type="tel" value={formData.phone || ''} onChange={e => setFormData({ ...formData, phone: e.target.value })} className="w-full p-2.5 shrink-0 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 outline-none focus:border-[#e51b23] focus:ring-1 focus:ring-[#e51b23] transition-all" /> : <p className="text-sm font-bold text-slate-900 py-1.5 break-words">{formData.phone || '-'}</p>}
                                            </div>
                                        </div>
                                    </div>


                                        {isNew && (
                                            <div className="pt-6 border-t border-slate-200 animate-fadeIn mt-8">
                                                <h4 className="text-[10px] font-black text-slate-900 uppercase tracking-widest mb-4 flex items-center gap-2">
                                                    <User size={14} className="text-emerald-500" /> Eerste Beheerder Uitnodigen (Optioneel)
                                                </h4>
                                                <div className="grid grid-cols-2 gap-4 bg-emerald-50/50 p-5 rounded-xl border border-emerald-100">
                                                    <div>
                                                        <label className="text-[9px] font-black text-emerald-700 uppercase tracking-widest block mb-1.5">Naam</label>
                                                        <input
                                                            type="text"
                                                            placeholder="Voornaam Achternaam"
                                                            value={inviteUserName}
                                                            onChange={e => setInviteUserName(e.target.value)}
                                                            className="w-full p-3 bg-white border border-emerald-100 rounded-lg text-xs font-bold text-slate-900 outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-all"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="text-[9px] font-black text-emerald-700 uppercase tracking-widest block mb-1.5">E-mailadres</label>
                                                        <input
                                                            type="email"
                                                            placeholder="admin@bedrijf.nl"
                                                            value={inviteUserEmail}
                                                            onChange={e => setInviteUserEmail(e.target.value)}
                                                            className="w-full p-3 bg-white border border-emerald-100 rounded-lg text-xs font-bold text-slate-900 outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-all"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === 'invoice' && (
                            <div className="bg-white rounded-2xl p-8 border border-slate-200 shadow-sm w-full space-y-8 animate-fadeIn">
                                <SectionHeader icon={<CreditCard size={16} />} title="Facturatie Informatie" />
                                <div className="space-y-6">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">KVK Nummer</label>
                                            {isEditing ? <input type="text" value={formData.invoice_kvk} onChange={e => setFormData({ ...formData, invoice_kvk: e.target.value })} className="w-full p-2.5 shrink-0 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 outline-none focus:border-[#e51b23] focus:ring-1 focus:ring-[#e51b23] transition-all" /> : <p className="text-sm font-bold text-slate-900 py-1.5 break-words">{formData.invoice_kvk || '-'}</p>}
                                        </div>
                                        <div>
                                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">BTW Nummer</label>
                                            {isEditing ? <input type="text" value={formData.invoice_vat} onChange={e => setFormData({ ...formData, invoice_vat: e.target.value })} className="w-full p-2.5 shrink-0 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 outline-none focus:border-[#e51b23] focus:ring-1 focus:ring-[#e51b23] transition-all" /> : <p className="text-sm font-bold text-slate-900 py-1.5 break-words">{formData.invoice_vat || '-'}</p>}
                                        </div>
                                    </div>
                                    <div>
                                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">Facturatie Email</label>
                                        {isEditing ? <input type="email" value={formData.invoice_email} onChange={e => setFormData({ ...formData, invoice_email: e.target.value })} className="w-full p-2.5 shrink-0 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 outline-none focus:border-[#e51b23] focus:ring-1 focus:ring-[#e51b23] transition-all" placeholder="factuur@bedrijf.nl" /> : <p className="text-sm font-bold text-slate-900 py-1.5 break-words">{formData.invoice_email || '-'}</p>}
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === 'content' && (
                            <div className="bg-white rounded-2xl p-8 border border-slate-200 shadow-sm w-full space-y-8 animate-fadeIn">
                                <SectionHeader icon={<FileText size={16} />} title="Content & Templates" />
                                
                                <div className="space-y-8">
                                    <div>
                                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">Standaard Vacature Opbouw</label>
                                        <p className="text-xs text-slate-500 mb-4 font-medium">Bedenk welke secties elke vacature standaard moet bevatten. Scheid ze met een komma.</p>
                                        <input
                                            type="text"
                                            value={(formData.vacancyStructure || []).join(', ')}
                                            onChange={e => {
                                                const arr = e.target.value.split(',').map(s => s.trim()).filter(s => s !== '');
                                                setFormData({ ...formData, vacancyStructure: arr });
                                            }}
                                            placeholder="Intro, Functieomschrijving, Functie-eisen..."
                                            className="w-full p-2.5 shrink-0 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 outline-none focus:border-[#e51b23] focus:ring-1 focus:ring-[#e51b23] transition-all"
                                        />
                                        <div className="flex flex-wrap gap-2 mt-3">
                                            {(formData.vacancyStructure || []).map((sec, idx) => (
                                                <span key={idx} className="px-3 py-1 bg-slate-100 text-slate-600 rounded-md text-[10px] font-bold uppercase tracking-widest">{sec}</span>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="border-t border-slate-200 pt-8">
                                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">Standaard Bedrijfsbeschrijving (AI Ondersteund)</label>
                                        <p className="text-xs text-slate-500 mb-4 font-medium">Deze tekst wordt gebruikt als context bij het genereren van AI-Vacatures voor deze organisatie.</p>
                                        <div className="bg-slate-50 border border-slate-200 rounded-xl h-[400px] overflow-hidden">
                                            <AiRichTextEditor
                                                value={formData.organizationDescription || ''}
                                                onChange={(val) => setFormData({ ...formData, organizationDescription: val })}
                                                placeholder="Beschrijf de cultuur, missie en voordelen van dit bedrijf..."
                                                minHeight="100%"
                                                className="border-none ring-0 h-full"
                                                aiMode="suggest"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === 'shortlist' && client?.id && (
                            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm animate-fadeIn min-h-[400px] flex flex-col overflow-hidden">
                                <div className="p-4 md:p-6 border-b border-slate-200 flex flex-col sm:flex-row sm:items-center justify-between gap-4 shrink-0">
                                    <div>
                                        <h3 className="text-xs font-black text-slate-900 uppercase tracking-widest flex items-center gap-2">
                                            <span className="text-[#e51b23]"><LayoutTemplate size={16} /></span>
                                            Shortlist ({clientApplications.length})
                                        </h3>
                                        <p className="text-[10px] text-slate-500 font-medium mt-1">
                                            Zoek op kandidaat, vacature, opdrachtgever of status.
                                        </p>
                                    </div>
                                    <div className="relative w-full sm:w-72 shrink-0">
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={14} />
                                        <input
                                            type="search"
                                            placeholder="Zoek kandidaat, vacature, klant…"
                                            value={shortlistSearch}
                                            onChange={(e) => setShortlistSearch(e.target.value)}
                                            className="w-full pl-9 pr-8 py-2 border border-slate-200 rounded-lg text-sm font-medium focus:outline-none focus:ring-1 focus:ring-[#e51b23] focus:border-[#e51b23] bg-slate-50"
                                        />
                                        {shortlistSearch ? (
                                            <button
                                                type="button"
                                                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                                onClick={() => setShortlistSearch('')}
                                                aria-label="Zoekopdracht wissen"
                                            >
                                                <X size={14} />
                                            </button>
                                        ) : null}
                                    </div>
                                </div>
                                <div className="flex-1 overflow-auto custom-scrollbar min-h-0">
                                    {isLoadingShortlist && clientApplications.length === 0 ? (
                                        <div className="flex flex-1 justify-center items-center p-12">
                                            <Loader2 className="animate-spin text-slate-400" />
                                        </div>
                                    ) : (
                                        <CandidateShortlistPhasesPanel
                                            applications={filteredShortlistBundle.applications}
                                            interviews={filteredShortlistBundle.interviews}
                                            placements={filteredShortlistBundle.placements}
                                            stageHistory={filteredShortlistBundle.stageHistory}
                                            scope={shortlistScope}
                                            onScopeChange={setShortlistScope}
                                            onOpenVacancy={openVacancy}
                                            onApplicationStatusClick={openShortlistMoveForApplication}
                                            focusPhase={shortlistFocusPhase}
                                            focusPhaseKey={shortlistFocusKey}
                                        />
                                    )}
                                </div>
                            </div>
                        )}

                        {activeTab === 'vacancies' && (
                            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm animate-fadeIn flex flex-col overflow-hidden min-h-[400px]">
                                <div className="p-4 md:p-6 border-b border-slate-200 flex flex-col sm:flex-row sm:items-center justify-between gap-4 shrink-0">
                                    <div>
                                        <h3 className="text-xs font-black text-slate-900 uppercase tracking-widest flex items-center gap-2">
                                            <span className="text-[#e51b23]"><Briefcase size={16} /></span>
                                            Vacatures ({clientVacancies.length})
                                        </h3>
                                        <p className="text-[10px] text-slate-500 font-medium mt-1">
                                            Zelfde overzicht als shortlist — met opdrachtgever en eindklant.
                                        </p>
                                    </div>
                                    <div className="relative w-full sm:w-72 shrink-0">
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={14} />
                                        <input
                                            type="search"
                                            placeholder="Zoek vacature, klant, status…"
                                            value={vacancySearch}
                                            onChange={(e) => setVacancySearch(e.target.value)}
                                            className="w-full pl-9 pr-8 py-2 border border-slate-200 rounded-lg text-sm font-medium focus:outline-none focus:ring-1 focus:ring-[#e51b23] focus:border-[#e51b23] bg-slate-50"
                                        />
                                        {vacancySearch ? (
                                            <button
                                                type="button"
                                                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                                onClick={() => setVacancySearch('')}
                                                aria-label="Zoekopdracht wissen"
                                            >
                                                <X size={14} />
                                            </button>
                                        ) : null}
                                    </div>
                                </div>
                                <div className="flex-1 overflow-auto custom-scrollbar p-4 md:p-6 min-h-0">
                                    {isLoadingVacancies ? (
                                        <div className="flex justify-center p-12">
                                            <Loader2 className="animate-spin text-slate-400" />
                                        </div>
                                    ) : (
                                        <ClientVacancyListTable
                                            vacancies={filteredClientVacancies}
                                            onOpenVacancy={openVacancy}
                                            resolveClient={resolveVacancyClient}
                                            emptyLabel={
                                                clientVacancies.length === 0
                                                    ? 'Geen vacatures gevonden voor dit bedrijf (opdrachtgever, eindklant of client).'
                                                    : 'Geen vacatures voor deze zoekopdracht.'
                                            }
                                        />
                                    )}
                                </div>
                            </div>
                        )}

                        {activeTab === 'users' && (
                            <div className="bg-white rounded-2xl p-8 border border-slate-200 shadow-sm animate-fadeIn w-full">
                                <SectionHeader icon={<Users size={16} />} title="Contactpersonen" />
                                {clientUsers.length === 0 ? (
                                    <div className="text-center p-8 text-slate-500 font-medium text-sm border-2 border-dashed border-slate-200 rounded-xl">Geen contactpersonen gevonden voor deze organisatie.</div>
                                ) : (
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                        {clientUsers.map(u => (
                                            <div key={u.id} className="p-4 border border-slate-200 rounded-xl bg-slate-50 flex items-start gap-4 hover:border-slate-200 transition-colors">
                                                <div className="w-10 h-10 rounded-full bg-slate-200 flex flex-col items-center justify-center text-slate-500 shrink-0 font-bold uppercase overflow-hidden">
                                                    {u.avatar_url ? <img src={u.avatar_url} className="w-full h-full object-cover" /> : (u.first_name ? u.first_name[0] : <User size={16} />)}
                                                </div>
                                                <div className="min-w-0">
                                                    <h4 className="font-bold text-slate-900 text-sm truncate">{u.first_name} {u.last_name}</h4>
                                                    {u.role && <p className="text-[10px] text-slate-400 font-black tracking-widest uppercase mb-1">{u.role}</p>}
                                                    <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-1 truncate"><Mail size={12} className="shrink-0"/> {u.email}</p>
                                                    {u.phone && <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-1"><Phone size={12} className="shrink-0"/> {u.phone}</p>}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <CandidateMoveModal
                isOpen={showMoveModal}
                onClose={() => {
                    setShowMoveModal(false);
                    setMoveDetails(null);
                }}
                moveDetails={moveDetails}
                templates={moveTemplates}
                currentUser={moveUser || ({ id: '', name: 'Recruiter', email: '', role: UserRole.RECRUITER, clientId: '' } as AppUser)}
                onConfirm={handleConfirmShortlistMove}
            />
        {/* AI Logo Search Modal */}
    {showLogoSearch && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-[500] p-4">
            <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden shadow-2xl animate-fadeIn">
                <div className="flex items-center justify-between p-6 bg-indigo-50 border-b border-indigo-100">
                    <div>
                        <h3 className="text-xl font-black text-indigo-900 tracking-tight flex items-center gap-2">
                            <Bot className="text-indigo-500" /> AI Logo Suggesties
                        </h3>
                        <p className="text-xs text-indigo-700/70 font-bold uppercase tracking-widest mt-1">Gevonden voor: {formData.name}</p>
                    </div>
                    <button onClick={() => setShowLogoSearch(false)} className="w-10 h-10 bg-white rounded-full flex items-center justify-center text-slate-400 hover:text-red-500 hover:bg-red-50 transition-all shadow-sm">
                        <X size={20} />
                    </button>
                </div>
                <div className="flex-1 overflow-y-auto p-6 bg-slate-50">
                    {isSearchingLogo ? (
                        <div className="flex flex-col items-center justify-center h-48 gap-4">
                            <Loader2 size={32} className="animate-spin text-indigo-500" />
                            <p className="text-sm font-bold text-slate-500">Afbeeldingen zoeken op het web...</p>
                        </div>
                    ) : logoSearchError ? (
                        <div className="bg-red-50 text-red-600 p-6 rounded-2xl border border-red-100 text-sm font-bold flex flex-col items-center justify-center gap-2 text-center h-48">
                            <AlertCircle size={32} />
                            {logoSearchError}
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                            {logoOptions.map((img, i) => (
                                <div key={i} onClick={() => handleSelectAILogo(img.url)} className="aspect-square bg-white border-2 border-slate-200 rounded-2xl hover:border-indigo-500 cursor-pointer overflow-hidden p-3 flex items-center justify-center group relative transition-all shadow-sm">
                                    <img 
                                        src={`${(import.meta as any).env.VITE_SERVER_URL || ''}/api/proxy-image?url=${encodeURIComponent(img.url)}`} 
                                        className="w-full h-full object-contain mix-blend-multiply transition-transform group-hover:scale-105" 
                                        alt="Logo Option" 
                                        referrerPolicy="no-referrer"
                                        loading="lazy"
                                        onError={(e) => {
                                            const target = e.target as HTMLImageElement;
                                            if (target.parentElement) target.parentElement.style.display = 'none';
                                        }}
                                    />
                                    <div className="absolute inset-0 bg-indigo-500/10 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    )}
    </div>
    );
};

export default ClientDetailModal;
